import { useEffect, useState } from 'react';
import { PlusIcon, ZapIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import type { TriggerProfileView } from '@/lib/api/generated/model';
import { PagePlaceholder } from '@/shared/components/PagePlaceholder';
import { ListCard } from '@/shared/components/ListCard';
import { TriggersMark } from '@/shared/components/emptyMarks';

import { isTriggerError } from '../errors';
import { useCreateTriggerProfile } from '../hooks/useCreateTriggerProfile';
import { useDeleteTriggerProfile } from '../hooks/useDeleteTriggerProfile';
import { useSetTriggerProfileEnabled } from '../hooks/useSetTriggerProfileEnabled';
import { useTriggerProfiles } from '../hooks/useTriggerProfiles';
import { useUpdateTriggerProfile } from '../hooks/useUpdateTriggerProfile';
import {
  toJiraTriggerProfileUpdate,
  toScheduledTriggerProfileUpdate,
  toTriggerProfileUpdate,
} from '../types';
import type {
  JiraTriggerProfileFormValues,
  ScheduledTriggerProfileFormValues,
  TriggerProfileFormValues,
} from '../types';
import { DeleteTriggerProfileDialog } from './DeleteTriggerProfileDialog';
import { TriggerProfileDetailSheet } from './TriggerProfileDetailSheet';
import { useTriggerDraftStore } from '../drafts/triggerDraftStore';
import {
  filterTriggers,
  triggerCounts,
  type TriggerStateFilter,
} from '../triggerFilters';
import { TriggerProfileFormSheet } from './TriggerProfileFormSheet';
import type { TriggerProfileFormMode } from './TriggerProfileFormSheet';
import { TriggerProfileList } from './TriggerProfileList';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * The workspace's trigger profiles — automation rules that watch a task
 * step transition and either create a new task or continue the source task.
 *
 * US1 (create), US2 (list/toggle), and US3 (run-history detail on row
 * click) are all wired here — the `triggers` package's own scope is
 * complete as of this page.
 *
 * One list card (spec 229 US2): search and the All / Enabled / Disabled
 * chips in its toolbar, the rows under them, and every other state —
 * loading, failed, empty, nothing matching — drawn inside the same card.
 * The page's "New trigger" top right is the only one: the empty state
 * never repeats it (FR-009).
 *
 * Four states, and the distinction that matters is the same one
 * `SecretsPage` documents: a failed load must never render as an empty
 * list. Presented as "you have none configured", it hides a genuine outage
 * behind an invitation to create a duplicate of something that already
 * exists.
 */
export function TriggersPage({
  workspaceId,
  createRequested = false,
  onCreateDismissed,
}: {
  workspaceId: string;
  /** The address asked for the create form (spec 129, from the palette). */
  createRequested?: boolean;
  onCreateDismissed?: () => void;
}) {
  const canWork = useCanWork();
  const { profiles, isPending, loadFailed, refetch } =
    useTriggerProfiles(workspaceId);

  const [dialog, setDialog] = useState<TriggerProfileFormMode | null>(() =>
    createRequested ? { kind: 'create' } : null,
  );

  // Spend the address's request once honoured, so a reload does not open
  // the form again (the Connections rule).
  useEffect(() => {
    if (createRequested) onCreateDismissed?.();
  }, [createRequested, onCreateDismissed]);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<TriggerStateFilter>('all');
  const [pendingDelete, setPendingDelete] = useState<TriggerProfileView | null>(
    null,
  );
  const create = useCreateTriggerProfile(workspaceId);
  const update = useUpdateTriggerProfile(workspaceId);
  const remove = useDeleteTriggerProfile(workspaceId);
  const setEnabled = useSetTriggerProfileEnabled(workspaceId);

  const saving = create.isPending || update.isPending;
  const saveError = dialog?.kind === 'edit' ? update.error : create.error;

  function openCreate() {
    create.reset();
    update.reset();
    setDialog({ kind: 'create' });
  }

  function openEdit(profile: TriggerProfileView) {
    create.reset();
    update.reset();
    setDialog({ kind: 'edit', profile });
  }

  function handleSubmit(
    values:
      | TriggerProfileFormValues
      | ScheduledTriggerProfileFormValues
      | JiraTriggerProfileFormValues,
  ) {
    if (dialog?.kind === 'edit') {
      const { id } = dialog.profile;
      const data =
        'project' in values
          ? toJiraTriggerProfileUpdate(values)
          : 'connectionId' in values
            ? toScheduledTriggerProfileUpdate(values)
            : toTriggerProfileUpdate(values);
      update.mutate(
        { triggerProfileId: id, data },
        {
          onSuccess: (profile) => {
            setDialog(null);
            toast.success(`Updated ${profile.name}.`);
          },
          // No onError: the sheet stays open showing the typed refusal, with
          // the entered configuration intact.
        },
      );
      return;
    }

    create.mutate(values, {
      onSuccess: (profile) => {
        // The one place that knows the create succeeded (spec 124 FR-011).
        useTriggerDraftStore.getState().clearDraft(workspaceId);
        setDialog(null);
        toast.success(`Created ${profile.name}.`);
      },
    });
  }

  function handleDelete() {
    if (!pendingDelete) return;
    const { name } = pendingDelete;
    remove.mutate(pendingDelete.id, {
      onSuccess: () => {
        setPendingDelete(null);
        toast.success(`Deleted ${name}.`);
      },
    });
  }

  function cancelDelete() {
    setPendingDelete(null);
    // A refusal belongs to the delete that drew it; the next one starts clean.
    remove.reset();
  }

  // A refusal keeps the dialog open with the service's reason in it
  // (spec 129 FR-005), rather than closing it and toasting.
  const deleteError =
    remove.error === null
      ? null
      : isTriggerError(remove.error)
        ? remove.error.message
        : `${pendingDelete?.name ?? 'It'} could not be deleted.`;

  function toggleEnabled(profile: TriggerProfileView) {
    const nextEnabled = !profile.enabled;
    setEnabled.mutate(
      { triggerProfileId: profile.id, enabled: nextEnabled },
      {
        onSuccess: () => {
          toast.success(
            nextEnabled
              ? `Enabled ${profile.name}.`
              : `Disabled ${profile.name}.`,
          );
        },
        onError: (error) => {
          const message = isTriggerError(error)
            ? error.message
            : `${profile.name} could not be updated.`;
          toast.error(message);
        },
      },
    );
  }

  const all = profiles ?? [];
  const shown = filterTriggers(all, query, stateFilter);
  const counts = triggerCounts(all);
  const chipLabel = { all: 'All', enabled: 'Enabled', disabled: 'Disabled' }[
    stateFilter
  ];
  const needle = query.trim();
  const noResults =
    needle === ''
      ? `No ${chipLabel.toLowerCase()} trigger here.`
      : `No trigger matches “${needle}”${stateFilter === 'all' ? '' : ` among ${chipLabel.toLowerCase()}`}.`;

  return (
    <PagePlaceholder
      eyebrow="Workspace"
      title="Triggers"
      description="Automation rules that react to a task moving between steps — create a follow-up task, or continue the same one with a comment and a move."
      icon={ZapIcon}
      actions={
        canWork ? (
          <Button size="sm" className="max-md:w-full" onClick={openCreate}>
            <PlusIcon aria-hidden="true" />
            New trigger
          </Button>
        ) : undefined
      }
    >
      <ListCard
        title="Triggers"
        count={isPending || loadFailed ? undefined : all.length}
        noun={['trigger', 'triggers']}
        search={{
          value: query,
          onChange: setQuery,
          placeholder: 'Search by name',
          label: 'Search triggers',
        }}
        chips={{
          label: 'Show',
          options: [
            { value: 'all', label: 'All', count: counts.all },
            { value: 'enabled', label: 'Enabled', count: counts.enabled },
            { value: 'disabled', label: 'Disabled', count: counts.disabled },
          ],
          value: stateFilter,
          onChange: setStateFilter,
        }}
        shown={shown.length}
        total={all.length}
        state={
          isPending
            ? 'loading'
            : loadFailed
              ? 'failed'
              : all.length === 0
                ? 'empty'
                : 'idle'
        }
        failedText="The triggers could not be loaded."
        onRetry={() => void refetch()}
        empty={{
          mark: <TriggersMark />,
          title: 'No triggers yet.',
          description:
            'A trigger reacts to a task moving between steps, or runs on a schedule.',
        }}
        noResults={{
          description: noResults,
          onClear: () => {
            setQuery('');
            setStateFilter('all');
          },
        }}
        className="w-full"
      >
        <TriggerProfileList
          workspaceId={workspaceId}
          profiles={shown}
          onToggleEnabled={toggleEnabled}
          // `mutation.variables` outlives the mutation itself (TanStack
          // Query keeps the last call's variables until the next `mutate`
          // or an explicit `reset()`), so it must be gated on `isPending`
          // here — otherwise the row that was last toggled would stay
          // disabled forever after its mutation had already settled.
          togglingId={
            setEnabled.isPending
              ? (setEnabled.variables?.triggerProfileId ?? null)
              : null
          }
          onSelect={(profile) => {
            setViewingId(profile.id);
          }}
          onEdit={openEdit}
          onDelete={setPendingDelete}
        />
      </ListCard>

      {viewingId ? (
        <TriggerProfileDetailSheet
          workspaceId={workspaceId}
          triggerProfileId={viewingId}
          open
          onCancel={() => {
            setViewingId(null);
          }}
        />
      ) : null}

      {dialog ? (
        <TriggerProfileFormSheet
          // Remount per target so the form re-seeds from the profile being
          // edited, rather than an effect writing state after the first
          // render (mirrors `ConnectionsPage`'s own convention).
          key={dialog.kind === 'edit' ? `edit:${dialog.profile.id}` : 'create'}
          mode={dialog}
          workspaceId={workspaceId}
          open
          isPending={saving}
          error={saveError}
          onSubmit={handleSubmit}
          onClearError={dialog.kind === 'edit' ? update.reset : create.reset}
          onCancel={() => {
            setDialog(null);
          }}
        />
      ) : null}

      {pendingDelete ? (
        <DeleteTriggerProfileDialog
          profileName={pendingDelete.name}
          open
          isPending={remove.isPending}
          onConfirm={handleDelete}
          error={deleteError}
          onCancel={cancelDelete}
        />
      ) : null}
    </PagePlaceholder>
  );
}
